import { useEffect, useRef } from 'react';

import * as Sentry from '@sentry/browser';

import { useAppState } from '../../AppStateProvider';
import { AppMode } from '../appStore';

import type { CustomScrapingEvent, CustomScrapingEventDetail } from './scrappingMonitoringService';
import ScrappingMonitoringService from './scrappingMonitoringService';

const SCRAPPING_TIMEOUT = 10000;

export const useScrapingMonitoring = () => {
  const { url, mode } = useAppState();
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  const eventReceived = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (mode !== AppMode.Extension) {
      return;
    }

    const handleScrapingEvent = (event: Event) => {
      const customEvent = event as CustomScrapingEvent;
      const data = (customEvent.detail as CustomScrapingEventDetail)?.data;
      const url = (customEvent.detail as CustomScrapingEventDetail)?.url;

      if (!url) {
        return;
      }

      const { page, validators } = ScrappingMonitoringService.getScrappingConfig(url);

      validators.forEach(({ name, validator }) => {
        if (!eventReceived.current[name]) {
          eventReceived.current[name] = true;
          timeoutRefs.current[name] = setTimeout(() => {
            delete timeoutRefs.current[name];
            delete eventReceived.current[name];
            console.warn(`Scrapping timeout exceeded for "${page}" page for "${name}" selector  with no valid data.`);
            Sentry.captureMessage(
              `Scrapping timeout exceeded for "${page}" page for "${name}" selector  with no valid data.`,
              {
                level: 'warning',
                extra: { url: url.href },
              }
            );
          }, SCRAPPING_TIMEOUT);
        }

        if (timeoutRefs.current[name] && validator(data)) {
          clearTimeout(timeoutRefs.current[name]);
          delete timeoutRefs.current[name];
          delete eventReceived.current[name];
        }
      });
    };

    document.addEventListener('scrapingEvent', handleScrapingEvent);

    return () => {
      if (mode !== AppMode.Extension) {
        return;
      }

      document.removeEventListener('scrapingEvent', handleScrapingEvent);
      Object.values(timeoutRefs.current).forEach(clearTimeout);
    };
  }, [mode]);

  useEffect(() => {
    if (mode !== AppMode.Extension) {
      return;
    }

    Object.values(timeoutRefs.current).forEach(clearTimeout);
    timeoutRefs.current = {};
    eventReceived.current = {};
  }, [url]);
};
